/**
 * WebAuthn PRF Encryption Utilities
 *
 * Provides AES-GCM encryption/decryption using keys derived from WebAuthn PRF extension.
 *
 * IMPORTANT ARCHITECTURE NOTES:
 * - LOCAL STORAGE: Encrypted with PRF-derived key (fast biometric unlock)
 * - EXPORTED FILES: Always encrypted with password (bitcoin-backup library)
 *
 * NEVER export PRF-encrypted backups - they're unrecoverable without the passkey
 * If user deletes passkey, local backup is lost - exported backup survives
 *
 * @see https://blog.millerti.me/2023/01/22/encrypting-data-in-the-browser-using-webauthn/
 */

import { Utils } from "@bsv/sdk";
import type { PRFEncryptedBackup } from "./types";

/**
 * Derive an AES-GCM encryption key from PRF output using HKDF
 *
 * @param prfOutput - The 32-byte PRF output from WebAuthn authentication
 * @returns CryptoKey suitable for AES-GCM encryption/decryption
 */
export async function deriveEncryptionKeyFromPRF(
	prfOutput: ArrayBuffer
): Promise<CryptoKey> {
	// Import PRF output as HKDF key material
	const keyDerivationKey = await crypto.subtle.importKey(
		"raw",
		prfOutput,
		"HKDF",
		false,
		["deriveKey"]
	);

	// Derive AES-GCM key using HKDF
	// Using empty salt and "sigma-backup-encryption" as info
	const encryptionKey = await crypto.subtle.deriveKey(
		{
			name: "HKDF",
			info: new TextEncoder().encode("sigma-backup-encryption"),
			salt: new Uint8Array(0),
			hash: "SHA-256",
		},
		keyDerivationKey,
		{ name: "AES-GCM", length: 256 },
		false, // Not extractable
		["encrypt", "decrypt"]
	);

	return encryptionKey;
}

/**
 * Encrypt data using PRF-derived key
 *
 * @param data - Data to encrypt (will be JSON stringified if object)
 * @param encryptionKey - AES-GCM key derived from PRF
 * @param credentialId - The credential ID used for encryption (for backup envelope)
 * @returns PRF encrypted backup envelope
 */
export async function encryptWithPRF(
	data: unknown,
	encryptionKey: CryptoKey,
	credentialId: string
): Promise<PRFEncryptedBackup> {
	// Convert data to bytes
	const dataString = typeof data === "string" ? data : JSON.stringify(data);
	const dataBytes = new TextEncoder().encode(dataString);

	// Generate random 12-byte nonce for AES-GCM
	const nonce = crypto.getRandomValues(new Uint8Array(12));

	// Encrypt
	const ciphertext = await crypto.subtle.encrypt(
		{ name: "AES-GCM", iv: nonce },
		encryptionKey,
		dataBytes
	);

	return {
		version: 2,
		encryption: "prf",
		credentialId,
		nonce: arrayBufferToBase64(nonce.buffer),
		ciphertext: arrayBufferToBase64(ciphertext),
	};
}

/**
 * Decrypt PRF-encrypted data
 *
 * @param envelope - The PRF encrypted backup envelope
 * @param encryptionKey - AES-GCM key derived from PRF
 * @returns Decrypted data as string
 */
export async function decryptWithPRF(
	envelope: PRFEncryptedBackup,
	encryptionKey: CryptoKey
): Promise<string> {
	const nonce = base64ToArrayBuffer(envelope.nonce);
	const ciphertext = base64ToArrayBuffer(envelope.ciphertext);

	const decrypted = await crypto.subtle.decrypt(
		{ name: "AES-GCM", iv: nonce },
		encryptionKey,
		ciphertext
	);

	return new TextDecoder().decode(decrypted);
}

/**
 * Generate a random PRF salt for a new credential
 * This salt should be stored with the credential and used for all PRF evaluations
 *
 * @returns 32-byte random salt as ArrayBuffer
 */
export function generatePRFSalt(): ArrayBuffer {
	return crypto.getRandomValues(new Uint8Array(32)).buffer;
}

/**
 * Convert ArrayBuffer to base64 string using BSV SDK Utils
 */
export function arrayBufferToBase64(buffer: ArrayBuffer): string {
	const bytes = new Uint8Array(buffer);
	const numberArray: number[] = Array.from(bytes);
	return Utils.toBase64(numberArray);
}

/**
 * Convert base64 string to ArrayBuffer using BSV SDK Utils
 */
export function base64ToArrayBuffer(base64: string): ArrayBuffer {
	const numberArray = Utils.toArray(base64, "base64");
	const bytes = new Uint8Array(numberArray);
	return bytes.buffer;
}

/**
 * Convert ArrayBuffer to base64url string (for WebAuthn credential IDs)
 */
export function arrayBufferToBase64URL(buffer: ArrayBuffer): string {
	const base64 = arrayBufferToBase64(buffer);
	return base64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=/g, "");
}

/**
 * Convert base64url string to ArrayBuffer
 */
export function base64URLToArrayBuffer(base64url: string): ArrayBuffer {
	// Add padding if needed
	let base64 = base64url.replace(/-/g, "+").replace(/_/g, "/");
	while (base64.length % 4) {
		base64 += "=";
	}
	return base64ToArrayBuffer(base64);
}
